import { useEffect } from 'react';
import type { ReactElement } from 'react';
import type { FieldProps } from '@rjsf/utils';
import { useSearchParams } from 'react-router-dom';

import useOrganisationInviteLink from 'app/hooks/useOrganisationInviteLink';

// Shape of each item in the array this field manages.
// Schema must include `minItems: 2` so that an empty array (produced when the
// invite link is unavailable) fails RJSF validation and blocks form submission.
//
//   { type: "array", minItems: 2,
//     items: { type: "object", required: ["appType","url"],
//              properties: { url: { type: "string" }, appType: { type: "string" } } } }
export interface AppLink {
  url: string;
  appType: string;
}

const APP_TYPES = ['ios', 'android', 'default'] as const;

// Stores the selected organisation's invite link — the same short URL that
// Marketing Material > Download materials shows next to the QR code — as hidden
// { url, appType } entries. All three app types carry that one link: it resolves
// to the right store per device and nominates the patient to the organisation.
// If the organisation has no invite link (or it fails to load), outputs [] and
// shows an error — pair with minItems: 2 in the schema to block form submission.
// Use in uiSchema as `"ui:field": "appLinks"`.
export function AppLinksField({
  formData,
  onChange,
  fieldPathId,
}: FieldProps<AppLink[]>): ReactElement {
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { inviteLink, isLoading, isError } = useOrganisationInviteLink(organisationId);

  const settled = Boolean(organisationId) && !isLoading;
  const hasNoInviteLink = settled && !isError && !inviteLink;

  useEffect(() => {
    if (!settled) return;

    // No usable link — a missing one or a failed fetch. Output an empty array so a
    // stale link cannot be submitted: this fails minItems: 2 and blocks submission.
    if (!inviteLink) {
      onChange([], fieldPathId.path);
      return;
    }

    onChange(
      APP_TYPES.map((appType) => ({ url: inviteLink, appType })),
      fieldPathId.path
    );
    // onChange is stable in RJSF; the settled link is the sole trigger for this sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, inviteLink]);

  const items = formData ?? [];

  return (
    <>
      {hasNoInviteLink && (
        <p className="vtwo:text-sm vtwo:text-red-600 vtwo:m-0">
          This organisation has no app download link. Cannot add the section.
        </p>
      )}
      {isError && (
        <p className="vtwo:text-sm vtwo:text-red-600 vtwo:m-0">
          Failed to load the app download link. Cannot add the section.
        </p>
      )}
      {items.map((link, i) => (
        <span key={`${link.appType}-${i}`}>
          <input
            type="hidden"
            id={`app_links_${i}_url`}
            name={`app_links_${i}_url`}
            value={link.url}
            readOnly
          />
          <input
            type="hidden"
            id={`app_links_${i}_appType`}
            name={`app_links_${i}_appType`}
            value={link.appType}
            readOnly
          />
        </span>
      ))}
    </>
  );
}
